import { TextField } from '@mui/material';
import cn from 'classnames';
import { useRef, useState } from 'react';

import { Button } from '@/components/Button';
import { IssueMarkdown } from '@/tracker/entities/issue';
import { isMac } from '@/utils/isMac';

import { MARKDOWN_ACTIONS, MarkdownAction, MarkdownToolbar } from '../MarkdownToolbar';

import s from './TextEditor.module.scss';

// Рамку и кольцо фокуса рисует обёртка вместе с панелью форматирования
const BOXED_INPUT_SX = {
  '& fieldset': { border: 'none' },
  '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none' },
};

type Props = {
  initialValue: string;
  ariaLabel: string;
  placeholder?: string;
  /**
   * Многострочный текст разметкой YFM: панель форматирования, сохранение кнопкой или Cmd/Ctrl+Enter,
   * без сохранения по потере фокуса
   */
  multiline?: boolean;
  /** Высота многострочного поля в строках */
  minRows?: number;
  submitLabel?: string;
  /** Текст ошибки или null, если значение годится */
  validate?: (value: string) => string | null;
  /** true — сохранено, редактор можно закрыть */
  onSave: (value: string) => Promise<boolean>;
  onClose: () => void;
};

/** Правка текста на месте. Однострочный: Enter или потеря фокуса — сохранить, Esc — отменить */
const TextEditor: React.FC<Props> = ({
  initialValue,
  ariaLabel,
  placeholder,
  multiline = false,
  minRows = 8,
  submitLabel = 'Сохранить',
  validate,
  onSave,
  onClose,
}) => {
  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isPreview, setIsPreview] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement>(null);
  const hasFocusedRef = useRef(false);

  // Поле не размонтируется на время просмотра: иначе пропадут история правок (Cmd+Z) и курсор
  const togglePreview = () => {
    setIsPreview(!isPreview);

    if (isPreview) requestAnimationFrame(() => inputRef.current?.focus());
  };

  const save = async () => {
    if (isSaving) return;

    if (value === initialValue) {
      onClose();

      return;
    }

    const validationError = validate?.(value) ?? null;

    if (validationError) {
      setError(validationError);

      return;
    }

    setIsSaving(true);

    if (await onSave(value)) {
      onClose();

      return;
    }

    setIsSaving(false);
  };

  const applyAction = (action: MarkdownAction) => {
    const input = inputRef.current;

    if (!input) return;

    const edit = action.edit({
      value: input.value,
      start: input.selectionStart ?? 0,
      end: input.selectionEnd ?? 0,
    });

    input.focus();
    input.setSelectionRange(edit.from, edit.to);

    // insertText идёт через историю правок поля: Cmd+Z отменяет разметку. Не сработал — меняем значение
    if (!document.execCommand('insertText', false, edit.insert)) {
      setValue(input.value.slice(0, edit.from) + edit.insert + input.value.slice(edit.to));
    }

    // После перерисовки — иначе React вернёт курсор в конец
    requestAnimationFrame(() => input.setSelectionRange(...edit.select));
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const isMod = event.metaKey || event.ctrlKey;
    // По коду клавиши, а не символу: Cmd+B работает и в русской раскладке
    const action = multiline && isMod && MARKDOWN_ACTIONS.find((a) => a.hotkey === event.code);

    if (action) {
      event.preventDefault();
      applyAction(action);

      return;
    }

    if (event.key !== 'Enter') return;

    if (multiline && !isMod) return;

    event.preventDefault();
    save();
  };

  return (
    <div className={s.editor}>
      <div className={cn(multiline && s.editor__box, error && s.editor__box_error)}>
        {multiline && (
          <MarkdownToolbar
            disabled={isSaving}
            isPreview={isPreview}
            onAction={applyAction}
            onTogglePreview={togglePreview}
          />
        )}
        {isPreview && (
          <div className={s.editor__preview} style={{ minHeight: `calc(${minRows} * 1.4375rem)` }}>
            <IssueMarkdown text={value} emptyText="Нечего показывать" />
          </div>
        )}
        <TextField
          inputRef={inputRef}
          autoFocus
          fullWidth
          size="small"
          multiline={multiline}
          minRows={multiline ? minRows : undefined}
          placeholder={placeholder}
          value={value}
          disabled={isSaving}
          error={!!error}
          helperText={error}
          onChange={(event) => {
            setValue(event.target.value);
            setError(null);
          }}
          onKeyDown={handleKeyDown}
          onBlur={multiline ? undefined : save}
          // Однострочный — выделить целиком; многострочный при открытии — курсор в конец, дописывать
          onFocus={(event) => {
            const { length } = event.target.value;

            if (!multiline) event.target.select();
            else if (!hasFocusedRef.current) event.target.setSelectionRange(length, length);

            hasFocusedRef.current = true;
          }}
          slotProps={{ htmlInput: { 'aria-label': ariaLabel } }}
          sx={[multiline && BOXED_INPUT_SX, isPreview && { display: 'none' }]}
        />
      </div>
      {multiline && (
        <div className={s.editor__actions}>
          <Button onClick={save} disabled={isSaving}>
            {submitLabel}
          </Button>
          <Button variant="outlined" onClick={onClose} disabled={isSaving}>
            Отмена
          </Button>
          <span className={s.editor__hint}>
            {isMac ? '⌘' : 'Ctrl'}+Enter — {submitLabel.toLowerCase()}
          </span>
        </div>
      )}
    </div>
  );
};

export { TextEditor };
